import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Upload, ListTodo, Home, Settings, LogOut, User, LogIn, BarChart3, Menu, X, ArrowUp, ScrollText, Users, Sun, Moon, Bell, BellOff } from 'lucide-react';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/useAuth';
import { useTheme } from '../hooks/useTheme';
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getPushStatus } from '../services/pushNotifications';
import Button from './Button';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { setTheme, isDark } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const pushSupported = isPushSupported();

  const mainNavItems = useMemo(
    () => [
      { path: '/', label: 'Серии', icon: Home },
      { path: '/teams', label: 'Команды', icon: Users },
      { path: '/upload', label: 'Загрузка', icon: Upload },
      { path: '/jobs', label: 'Задачи', icon: ListTodo },
    ],
    []
  );

  const adminNavItems = useMemo(
    () =>
      isAdmin
        ? [
            { path: '/statistics', label: 'Статистика', icon: BarChart3 },
            { path: '/audit', label: 'Логи', icon: ScrollText },
            { path: '/admin', label: 'Админ', icon: Settings },
          ]
        : [],
    [isAdmin]
  );

  const mobileNavItems = useMemo(() => [...mainNavItems, ...adminNavItems], [adminNavItems, mainNavItems]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const isNavItemActive = (itemPath: string) => {
    const pathname = location.pathname;
    if (itemPath === '/') return pathname === '/' || pathname.startsWith('/series/');
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
  };

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    if (isMobileMenuOpen) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isAuthenticated && pushSupported) {
      getPushStatus().then(s => setPushSubscribed(s.subscribed));
    }
  }, [isAuthenticated, pushSupported]);

  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
      } else {
        const ok = await subscribeToPush();
        setPushSubscribed(ok);
      }
    } finally {
      setPushLoading(false);
    }
  };

  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 600);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <nav className="sticky top-0 z-40 border-b border-gray-200/70 dark:border-gray-700/70 bg-white/80 dark:bg-gray-900/60 backdrop-blur supports-[backdrop-filter]:bg-white/70 supports-[backdrop-filter]:dark:bg-gray-900/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 safe-px">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex-shrink-0 flex items-center cursor-pointer hover:opacity-90 transition-opacity">
                <BookOpen className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white hidden sm:inline">
                  Manga Translator
                </span>
              </Link>
              <div className="hidden sm:flex items-center gap-2">
                {mainNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isNavItemActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={item.label}
                      aria-label={item.label}
                      className={clsx(
                        'relative group inline-flex items-center justify-center h-11 w-11 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
                        isActive
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800/70 dark:hover:text-white'
                      )}
                    >
                      <Icon className="h-6 w-6" />
                      <span className="pointer-events-none absolute top-full mt-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              {isAuthenticated ? (
                <>
                  {adminNavItems.length > 0 && (
                    <div className="hidden sm:flex items-center gap-2">
                      {adminNavItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = isNavItemActive(item.path);
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            title={item.label}
                            aria-label={item.label}
                            className={clsx(
                              'relative group inline-flex items-center justify-center h-11 w-11 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
                              isActive
                                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800/70 dark:hover:text-white'
                            )}
                          >
                            <Icon className="h-6 w-6" />
                            <span className="pointer-events-none absolute top-full mt-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                              {item.label}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}

                  <div className="hidden sm:flex items-center gap-2 rounded-xl bg-gray-100/70 dark:bg-gray-800/60 px-3 py-2 text-sm text-gray-800 dark:text-gray-200">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/70 dark:bg-gray-900/40 ring-1 ring-gray-200 dark:ring-gray-700">
                      <User className="h-4 w-4" />
                    </span>
                    <span className="hidden md:inline max-w-[18rem] truncate">{user?.email}</span>
                    {isAdmin && <span className="ml-1 rounded-md bg-primary-100 dark:bg-primary-900 px-2 py-0.5 text-xs text-primary-800 dark:text-primary-200">Admin</span>}
                  </div>
                  {pushSupported && (
                    <button
                      type="button"
                      onClick={handleTogglePush}
                      disabled={pushLoading}
                      className={clsx(
                        'relative group inline-flex items-center justify-center h-11 w-11 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
                        pushSubscribed
                          ? 'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/30'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800/70 dark:hover:text-white'
                      )}
                      aria-label={pushSubscribed ? 'Отключить уведомления' : 'Включить уведомления'}
                    >
                      {pushLoading ? (
                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : pushSubscribed ? (
                        <Bell className="h-5 w-5" />
                      ) : (
                        <BellOff className="h-5 w-5" />
                      )}
                      <span className="pointer-events-none absolute top-full mt-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        {pushSubscribed ? 'Отключить уведомления' : 'Включить уведомления'}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setTheme(isDark ? 'light' : 'dark')}
                    className="relative group inline-flex items-center justify-center h-11 w-11 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800/70 dark:hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label={isDark ? 'Светлая тема' : 'Тёмная тема'}
                  >
                    {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                    <span className="pointer-events-none absolute top-full mt-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                      {isDark ? 'Светлая тема' : 'Тёмная тема'}
                    </span>
                  </button>
                  <Button variant="secondary" size="sm" className="h-11 rounded-xl px-4" onClick={handleLogout}>
                    <LogOut className="h-4 w-4 mr-0 sm:mr-2" />
                    <span className="hidden sm:inline">Выход</span>
                  </Button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setTheme(isDark ? 'light' : 'dark')}
                    className="relative group inline-flex items-center justify-center h-11 w-11 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800/70 dark:hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label={isDark ? 'Светлая тема' : 'Тёмная тема'}
                  >
                    {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  </button>
                  <Link to="/login">
                    <Button variant="secondary" size="sm">
                      <LogIn className="h-4 w-4 mr-0 sm:mr-2" />
                      Войти
                    </Button>
                  </Link>
                </>
              )}
              <button
                type="button"
                className="sm:hidden inline-flex items-center justify-center rounded-lg p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Открыть меню"
                aria-expanded={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 sm:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={closeMobileMenu} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[80vw] bg-white dark:bg-gray-800 shadow-xl flex flex-col">
            <div className="h-16 px-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
              <Link to="/" onClick={closeMobileMenu} className="flex items-center hover:opacity-80 transition-opacity">
                <BookOpen className="h-7 w-7 text-primary-600 dark:text-primary-400" />
                <span className="ml-2 text-lg font-bold text-gray-900 dark:text-white">Manga</span>
              </Link>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Закрыть меню"
                onClick={closeMobileMenu}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-2 py-3">
              <div className="space-y-1">
                {mobileNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isNavItemActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={clsx(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-base font-medium',
                        isActive
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                      )}
                    >
                      <Icon className="h-6 w-6" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 p-3 safe-pb">
              {isAuthenticated ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <User className="h-4 w-4" />
                    <span className="truncate">{user?.email}</span>
                    {isAdmin && (
                      <span className="ml-auto px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded">
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTheme(isDark ? 'light' : 'dark')}
                      className="flex items-center justify-center h-10 w-10 rounded-lg text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors"
                      aria-label={isDark ? 'Светлая тема' : 'Тёмная тема'}
                    >
                      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                    </button>
                    <Button variant="secondary" className="flex-1 flex items-center justify-center" onClick={handleLogout}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Выход
                    </Button>
                  </div>
                </div>
              ) : (
                <Link to="/login" onClick={closeMobileMenu}>
                  <Button variant="secondary" className="w-full flex items-center justify-center">
                    <LogIn className="h-4 w-4 mr-2" />
                    Войти
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 safe-px safe-pb py-4 sm:py-8">
        {children}
      </main>

      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="sm:hidden fixed right-4 bottom-4 z-30 h-11 w-11 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          style={{
            right: 'calc(1rem + env(safe-area-inset-right))',
            bottom: 'calc(1rem + env(safe-area-inset-bottom))',
          }}
          aria-label="Наверх"
        >
          <ArrowUp className="h-5 w-5 mx-auto" />
        </button>
      )}
    </div>
  );
}
